import React, { useEffect, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { getBusinessTransactions } from "api/business-user-management";
import {
  IBusinessTransactionResult,
  IBusinessTransactionsPayload,
} from "api/business-user-management/types";
import { ReactComponent as CreditICon } from "assets/icons/credit-icon.svg";
import { ReactComponent as DebitIcon } from "assets/icons/debit-icon.svg";
import { useApp } from "contexts/app-context";
import { useBusinesses } from "contexts/businesses-context";
import { TransactionsSubRouteName } from "features/business-transactions/content";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ROUTES } from "routes/constants";
import {
  capitalizeFirstLetter,
  formatDateTo12HourTime,
  formatDateToDDMMMYYYY,
  handleAmountFormat,
  handleTruncateAmount,
  replaceIdParamsInRouteWithValue,
} from "utils";
import { ONE_NAIRA_IN_KOBO, ZERO } from "utils/constants";

import { customErrorToast } from "ui/custom-toast";
import { DEFAULT_PAGINATION_DATA, Pagination } from "ui/pagination";
import { Status } from "ui/status";
import { StatusUiColorEnum, TStatus } from "ui/status/types";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TSelectedTableRowIds, TTableRowData } from "ui/table/types";

import { BusinessTransactionsTableHeaderCTAs } from "./BusinessTransactionsTableHeaderCTAs";
import {
  DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS,
  columns,
} from "./const";
import { TTransactionStatus, TTransactionType } from "./types";

export const transactionTypeIconMap: Record<TTransactionType, React.ReactNode> =
  {
    debit: <DebitIcon />,
    credit: <CreditICon />,
  };

export const transactionStatusToGeneralStatusMap: Record<
  TTransactionStatus,
  TStatus
> = {
  successful: StatusUiColorEnum.SUCCESS,
  failed: StatusUiColorEnum.ERROR,
  pending: StatusUiColorEnum.WARNING,
};

export const BusinessTransactionsTable = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const type = searchParams.get("type");
  const status = searchParams.get("status");
  const accountName = searchParams.get("accountName");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const search = searchParams.get("search");
  const filterRetryCount = searchParams.get("filterRetryCount");
  const { handleGetBusinessAccounts } = useBusinesses();
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [isLoadingBusinessTransactions, setIsLoadingBusinessTransactions] =
    useState(false);
  const [businessTransactionsData, setBusinessTransactionsData] =
    useState<IBusinessTransactionResult>({
      data: [],
      ...DEFAULT_PAGINATION_DATA,
    });
  const [selectedBusinessTransactionIds, setSelectedBusinessTransactionIds] =
    useState<TSelectedTableRowIds>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleGetBusinessTransactions = async (
    payload: IBusinessTransactionsPayload,
    others: {
      isRefetching: boolean;
    }
  ) => {
    const { isRefetching } = others;
    if (!isRefetching) {
      setIsLoadingBusinessTransactions(true);
    }

    try {
      const { error, data, message } = await getBusinessTransactions(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setBusinessTransactionsData(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }

    if (!isRefetching) {
      setIsLoadingBusinessTransactions(false);
    }
  };

  const { handleSaveTransactionRefId } = useApp();
  const handleViewTransactionDetails = (
    businessTransactionRowData: TTableRowData
  ) => {
    const transactionId = businessTransactionRowData["id"] as string;
    const transactionReference = businessTransactionRowData[
      "reference"
    ] as string;
    handleSaveTransactionRefId({
      reference: transactionReference,
      id: transactionId,
    });
    const transactionDetailsRoute = replaceIdParamsInRouteWithValue({
      route: ROUTES.TRANSACTION_DETAILS_PAGE.replace(
        ":transactionRef",
        transactionReference
      ),
      idParamsName: ":transactionType",
      value: TransactionsSubRouteName.TRANSFERS,
    });
    navigate(transactionDetailsRoute);
  };
  const { data: businessTransactions, ...paginationData } =
    businessTransactionsData;
  const formattedData = businessTransactions.map((transaction) => {
    return {
      "Date and Time": (
        <Box className="flex flex-col items-start">
          {transaction?.createdAt ? (
            <>
              <p className="text-sm text-[#09090B]">
                {formatDateToDDMMMYYYY(transaction?.createdAt)}
              </p>
              <p className="text-[12px] leading-[16px] text-[#807F94]">
                {formatDateTo12HourTime(transaction?.createdAt)}
              </p>
            </>
          ) : (
            "N/A"
          )}
        </Box>
      ),
      Type: (
        <Box className="flex items-center gap-3">
          {transaction?.type ? (
            <>
              {transactionTypeIconMap[transaction?.type]}
              <p className="text-sm capitalize text-[#374151]">
                {transaction?.type}
              </p>
            </>
          ) : (
            "N/A"
          )}
        </Box>
      ),
      Description: (
        <Box className="flex flex-col justify-center items-start">
          <p className="text-sm text-[#101828]">
            {transaction?.type === "credit" ? "Transfer from" : "Transfer to"}
          </p>
          <p className="text-sm text-[#667085]">
            {transaction?.description || "N/A"}
          </p>
        </Box>
      ),
      "Source Account": (
        <p className="text-sm text-[#667085]">
          {transaction?.accountName || "N/A"}
        </p>
      ),
      Amount: (
        <p className="text-sm text-[#1A202C] font-semibold">
          {handleAmountFormat({
            value: String(
              handleTruncateAmount(transaction?.amount || ZERO) /
                ONE_NAIRA_IN_KOBO
            ),
            includeNairaSymbol: true,
            shouldFormatZero: true,
            isFormattedAmtNotPadded: true,
          })}
        </p>
      ),
      "Current Balance": (
        <p className="text-sm text-[#1A202C]">
          {handleAmountFormat({
            value: String(
              handleTruncateAmount(transaction?.newBalance || ZERO) /
                ONE_NAIRA_IN_KOBO
            ),
            includeNairaSymbol: true,
            shouldFormatZero: true,
            isFormattedAmtNotPadded: true,
          })}
        </p>
      ),
      Status: transaction?.status ? (
        <Status
          status={transactionStatusToGeneralStatusMap[transaction?.status]}
          text={capitalizeFirstLetter(transaction?.status)}
          className="w-fit"
        />
      ) : (
        "N/A"
      ),
      id: transaction?.id || "",
      reference: transaction?.reference || "",
    };
  });

  useEffect(() => {
    if (!businessId) return;
    const payload: IBusinessTransactionsPayload = {
      businessId,
      page: Number(
        page || DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.page
      ),
      limit: Number(
        limit || DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.limit
      ),
    };

    if (search) {
      payload["search"] = search;
    }

    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }

    if (type) {
      const transactionTypes = type.split(",");
      const containsCredit = transactionTypes.includes("credit");
      const containsDebit = transactionTypes.includes("debit");
      if (
        !transactionTypes.includes("all") &&
        containsCredit !== containsDebit
      ) {
        const [typeToSend] = transactionTypes;
        payload["type"] = typeToSend as TTransactionType;
      }
    }

    if (status) {
      const transactionStatus = status.split(",");
      if (!transactionStatus.includes("all")) {
        payload["status"] = transactionStatus as TTransactionStatus[];
      }
    }

    if (accountName) {
      payload["accountName"] = accountName;
    }

    void handleGetBusinessTransactions(payload, {
      isRefetching: false,
    });
  }, [
    businessId,
    page,
    limit,
    type,
    status,
    startDate,
    endDate,
    accountName,
    search,
    filterRetryCount,
  ]);

  useEffect(() => {
    if (!businessId) return;
    void handleGetBusinessAccounts({ businessId });
  }, [businessId]);

  return (
    <TableWrapper className="!p-0">
      <BusinessTransactionsTableHeaderCTAs
        handleGetBusinessTransactions={handleGetBusinessTransactions}
        isLoading={isLoadingBusinessTransactions}
        isRefreshing={isRefreshing}
        setIsRefreshing={setIsRefreshing}
      />
      <Table
        columns={columns}
        data={formattedData}
        onRowClick={handleViewTransactionDetails}
        isLoading={isLoadingBusinessTransactions && !isRefreshing}
        isRowClickable
        hasCheckbox
        selectedTableRowIds={selectedBusinessTransactionIds}
        setSelectedTableRowIds={setSelectedBusinessTransactionIds}
        tableRef={tableRef}
      />
      <Pagination
        paginationData={paginationData}
        data={businessTransactionsData}
        defaultPaginationParams={
          DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS
        }
        tableRef={tableRef}
        isLoadingTable={isLoadingBusinessTransactions}
      />
    </TableWrapper>
  );
};
